import styled, { keyframes } from 'styled-components'

interface IQuadrantTagContainer {
  alignITems?: string
}

interface IQuadrantTag {
  show?: boolean
}

interface IBodyQuadrant {
  left?: boolean
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 720px;
  padding: 28px 0 64px;
  position: relative;
  box-sizing: border-box;
`

export const QuadrantsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;
  min-height: 420px;
  gap: 34px;
  align-items: start;
  grid-auto-rows: 380px;
  box-sizing: border-box;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 24px;
    grid-auto-rows: 360px;
  }
`

export const BodyQuadrant = styled.div<IBodyQuadrant>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  flex: 1;
  direction: ltr;
  overflow: auto;
  padding: 8px 22px 44px;
  gap: 8px;
  min-height: 0;
  max-height: none;
  height: 100%;
  margin-top: 10px;
  border-top: 1px solid #edf3f8;

  &::-webkit-scrollbar {
    width: 10px;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    border-radius: 999px;
    background-color: rgba(221, 232, 241, 0.85);
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: linear-gradient(180deg, #81acc4 0%, #4d88ab 100%);
    border: 2px solid rgba(255, 255, 255, 0.85);
  }
`

export const QuadrantTagContainer = styled.div<IQuadrantTagContainer>`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: ${(props) => props.alignITems || 'flex-start'};
  position: relative;
  margin-top: 8px;
  margin-bottom: 6px;
`
export const QuadrantTag = styled.div<IQuadrantTag>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 136px;
  height: 38px;
  background-color: ${(props) => (props.show ? '#C6016B' : '#81acc4')};
  border-radius: 999px;
  color: white;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  position: relative;
  z-index: 3;
  box-shadow: 0px 8px 18px rgba(45, 116, 156, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  :active {
    box-shadow: 0 0px #666;
    transform: translateY(1px);
  }

  :hover {
    transform: translateY(-1px);
    box-shadow: 0px 10px 20px rgba(45, 116, 156, 0.28);
  }
`

export const AbsoluteContainer = styled.div`
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(209, 230, 241, 0.55) 0%, rgba(209, 230, 241, 0.18) 65%, rgba(209, 230, 241, 0) 100%);
  opacity: 0.7;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -40%);
  pointer-events: none;
  z-index: 0;

  @media (max-width: 1366px) {
    transform: translate(-50%, -42%);
  }

  @media (max-width: 960px) {
    width: 220px;
    height: 220px;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`

export const ShowName = styled.div<IBodyQuadrant>`
  font-size: 15px;
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  min-height: 24px;
  width: fit-content;
  max-width: 100%;
  color: #38536b;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &::before {
    content: ' • ';
    color: #2d749c;
  }
`

export const EmptyStateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: min(100%, 360px);
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(129, 172, 196, 0.2);
  background: linear-gradient(180deg, #f5fafe 0%, #edf5fb 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
`

export const EmptyStateTitle = styled.div`
  color: #2d5876;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
`

export const EmptyStateHint = styled.div`
  color: #6b8195;
  font-size: 13px;
  line-height: 1.45;
  text-align: center;
`

const scrollCueMotion = keyframes`
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.45;
  }

  50% {
    transform: translateY(4px);
    opacity: 1;
  }
`

export const ScrollCue = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  padding: 34px 14px 10px;
  background: linear-gradient(180deg, rgba(248, 251, 254, 0) 0%, rgba(248, 251, 254, 0.94) 42%, #f8fbfe 100%);
  pointer-events: none;
`

export const ScrollCueText = styled.div`
  color: #5c7b92;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: center;
`

export const ScrollCueChevrons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 0.7;
  color: #2d749c;
  font-size: 14px;
  animation: ${scrollCueMotion} 1.2s ease-in-out infinite;
`

export const NameContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: auto;
  width: 80%;
  height: 90%;
`

export const QuadrantCard = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-height: 380px;
  height: 380px;
  max-height: 380px;
  border-radius: 28px;
  padding: 24px 20px 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbfe 100%);
  border: 1px solid #dfe9f2;
  box-shadow: 0px 14px 28px rgba(31, 33, 36, 0.05);
  overflow: hidden;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 960px) {
    min-height: 360px;
    height: 360px;
    max-height: 360px;
  }
`

export const QuadrantHeader = styled.div<IBodyQuadrant>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 96px;
  padding: 0 132px 6px;
  text-align: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    min-height: auto;
    padding: 0 0 6px;
  }
`

export const QuadrantInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  text-align: center;
  min-width: 0;
`

export const QuadrantName = styled.h4`
  margin: 0;
  font-size: 27px;
  font-weight: 700;
  color: #23384f;
  text-transform: capitalize;
  line-height: 1.08;

  @media (max-width: 640px) {
    font-size: 24px;
  }
`

export const QuadrantDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: #708299;
  max-width: 340px;
  width: 100%;
`

export const QuadrantStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  position: absolute;
  top: 0;
  right: 0;
  max-width: 120px;

  @media (max-width: 640px) {
    position: static;
    align-items: center;
    max-width: none;
  }
`

export const QuadrantBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(45, 116, 156, 0.1);
  color: #2d749c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

export const QuadrantMeta = styled.span`
  font-size: 14px;
  color: #8a99ac;
`
